import { useCallback, useRef, useState } from "react";
import { useRestApiProvider, UserRole } from "@jugl-web/rest-api";
import {
  isAPIError,
  isErrorCodeOnlyAPIError,
  isValidEmail,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { PhoneInputValue } from "@jugl-web/ui-components/cross-platform";
import {
  AuthResponseProfile,
  useRequestOtpMutation,
  useVerifyOtpMutation,
} from "@web-src/features/auth/authApi";
import { useAppDispatch } from "@web-src/store";
import { proceedLogin } from "@web-src/features/auth/authSlice";
import {
  CX_BUSINESS_NAME_KEY,
  CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY,
  ENTITY_ID_KEY,
  JUGL_LOGIN,
  getStorageItem,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { setJwtToken } from "@web-src/features/auth/utils";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import ReCAPTCHA from "react-google-recaptcha";
import { environment } from "@web-src/environments/environment";
import isObject from "lodash/isObject";
import { LoginMethod } from "@web-src/modules/auth/pages/Login/types";

export type CXSignupStep =
  | "credentials"
  | "otp"
  | "workspace"
  | "workspaceLimit";

export const useCXSignupFlow = () => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const dispatch = useAppDispatch();
  const { entityId: anonymousEntityId } = useAnonymousSession();
  const { navigateToPage } = useNavigation();
  const { entitiesApi, usersApi } = useRestApiProvider();
  const [getAllEntities] =
    entitiesApi.useLazy_DEPRECATED_getAllOrgEntitiesListQuery();

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [step, setStep] = useState<CXSignupStep>("credentials");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    LoginMethod.email
  );
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState<PhoneInputValue>({
    code: "",
    iso: "",
    phone: "",
    isValid: false,
  });
  const [profile, setProfile] = useState<AuthResponseProfile["profile"]>();
  const [otpRequestId, setOtpRequestId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  const isCredentialsValid =
    loginMethod === LoginMethod.email
      ? isValidEmail(email)
      : phone.isValid === true;

  const [requestOtpMutation] = useRequestOtpMutation();
  const [verifyOtpMutation] = useVerifyOtpMutation();
  const [createProfile] = usersApi.useCreateProfileMutation();
  const [claimEntityMutation] = entitiesApi.useClaimEntityMutation();

  const anonymousEntityIdRef = useRef(anonymousEntityId);
  anonymousEntityIdRef.current = anonymousEntityId;

  const credentialsRef = useRef<{
    token: string;
    refreshToken: string;
    login: string;
  } | null>(null);

  const handleLogin = useCallback(() => {
    if (!credentialsRef.current) return;
    const { token, refreshToken, login } = credentialsRef.current;
    dispatch(proceedLogin({ token, refreshToken, login }));
  }, [dispatch]);

  const requestOtp = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      if (!isCredentialsValid) return;
      setIsLoading(true);
      let grcToken = "";

      try {
        if (
          environment.isReCaptchaEnabled &&
          !(
            loginMethod === LoginMethod.phone &&
            phone.phone &&
            /^000\d{4}000$/.test(phone.phone)
          )
        ) {
          grcToken = (await recaptchaRef.current?.executeAsync()) || "";
          if (!grcToken) {
            setIsLoading(false);
            return;
          }
        }
      } catch {
        setIsLoading(false);
        return;
      }

      const response = await requestOtpMutation({
        country: phone.iso,
        country_isd_code:
          loginMethod === LoginMethod.phone ? phone.code : undefined,
        mobile: loginMethod === LoginMethod.phone ? phone.phone : undefined,
        email: loginMethod === LoginMethod.email ? email : undefined,
        grc_token: grcToken,
      });

      recaptchaRef.current?.reset();

      if ("error" in response && response.error) {
        if (
          isErrorCodeOnlyAPIError(response.error) &&
          response.error.data.errors === "rate_limit_reached"
        ) {
          toast(
            t({
              id: "feedback.rate-limit-reached",
              defaultMessage: "You have reached the rate limit.",
            }),
            { variant: "error" }
          );
        } else {
          toast(
            t({
              id: "feedback.something-went-wrong",
              defaultMessage: "Something went wrong.",
            }),
            { variant: "error" }
          );
        }
        setIsLoading(false);
        return;
      }

      if ("data" in response && response.data) {
        setProfile(response.data.profile);
        setOtpRequestId(response.data.id);
        setStep("otp");
      }
      setIsLoading(false);
    },
    [
      email,
      isCredentialsValid,
      loginMethod,
      phone.code,
      phone.iso,
      phone.phone,
      requestOtpMutation,
      t,
      toast,
    ]
  );

  const verifyOtp = useCallback(
    async (otp: string) => {
      if (isLoading) return;
      setOtpError("");
      setIsLoading(true);

      const resp = await verifyOtpMutation({
        id: otpRequestId,
        otp,
      });

      if ("data" in resp) {
        const { token, refreshToken } = resp.data;
        const login = phone.isValid ? `${phone.code} ${phone.phone}` : email;
        saveItemToStorage(JUGL_LOGIN, login);
        // Store JWT in localStorage so axios can use it, but don't dispatch
        // proceedLogin yet — that triggers navigation and cleans up anonymous session
        setJwtToken(token, refreshToken);
        credentialsRef.current = { token, refreshToken, login };

        const entitiesResp = await getAllEntities();

        if (
          "data" in entitiesResp &&
          entitiesResp.data &&
          entitiesResp.data.filter((e) => e.role === UserRole.admin).length >= 5
        ) {
          setStep("workspaceLimit");
          return;
        }
        setStep("workspace");
      } else if (
        "data" in resp.error &&
        isObject(resp.error.data) &&
        "errors" in resp.error.data &&
        typeof resp.error.data.errors === "string"
      ) {
        setOtpError(resp.error.data.errors);
      }
      setIsLoading(false);
    },
    [
      email,
      getAllEntities,
      isLoading,
      otpRequestId,
      phone.code,
      phone.isValid,
      phone.phone,
      verifyOtpMutation,
    ]
  );

  const claimWorkspace = useCallback(
    async (params: {
      firstName: string;
      lastName: string;
      workspaceName: string;
      workspaceCountry: string;
      workspaceEmail: string;
    }): Promise<boolean> => {
      setIsLoading(true);

      try {
        // Create user profile first if it does not exist
        if (!profile) {
          await createProfile({
            first_name: params.firstName,
            last_name: params.lastName,
          }).unwrap();
        }

        // Claim the anonymous entity
        const entityId = anonymousEntityIdRef.current;
        const result = await claimEntityMutation({
          entityId,
          data: {
            country: params.workspaceCountry,
            name: params.workspaceName,
            billing_email: params.workspaceEmail ?? undefined,
          },
        });

        if ("data" in result) {
          saveItemToStorage(ENTITY_ID_KEY, entityId);
          if (credentialsRef.current) {
            await dispatch(proceedLogin(credentialsRef.current));
          }
          navigateToPage("cxAIAgents", undefined, { entityId });
          return true;
        }

        if (
          "error" in result &&
          isAPIError(result.error) &&
          result.error.data.errors === "max_limit_reached"
        ) {
          setStep("workspaceLimit");
          return false;
        }

        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong.",
          }),
          { variant: "error" }
        );
        return false;
      } catch {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong.",
          }),
          { variant: "error" }
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      claimEntityMutation,
      createProfile,
      dispatch,
      navigateToPage,
      profile,
      t,
      toast,
    ]
  );

  const initiateGoogleSignup = useCallback(() => {
    const businessName = getStorageItem<string>(CX_BUSINESS_NAME_KEY) || "";
    saveItemToStorage(CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY, {
      anonymousEntityId: anonymousEntityIdRef.current,
      businessName,
    });
    const redirectUrl = `${window.location.origin}/jugl-callback`;
    window.open(
      `${
        environment.apiUrl
      }/jugl/uberauth/google?final_callback_url=${encodeURIComponent(
        redirectUrl
      )}&agt=web`,
      "_self"
    );
  }, []);

  const goBackToCredentials = useCallback(() => {
    setOtpError("");
    setStep("credentials");
  }, []);

  const reset = useCallback(() => {
    setStep("credentials");
    setEmail("");
    setPhone({ code: "", iso: "", phone: "", isValid: false });
    setOtpRequestId("");
    setOtpError("");
    setIsLoading(false);
    credentialsRef.current = null;
  }, []);

  return {
    step,
    profile,
    loginMethod,
    email,
    phone,
    isLoading,
    otpError,
    recaptchaRef,
    isCredentialsValid,
    setLoginMethod,
    setEmail,
    setPhone,
    requestOtp,
    verifyOtp,
    claimWorkspace,
    initiateGoogleSignup,
    goBackToCredentials,
    reset,
    handleLogin,
  };
};
